import { useState, useEffect } from 'react';
import API_BASE from '../utils/api';

export const DEFAULT_PERMISSIONS = {
  // Section navigation (controls sidebar / route guards)
  dashboard: true,
  projects: true,
  library: true,
  timeBilling: true,
  planner: true,
  reports: true,
  settings: true,

  // Invoices & Proposals
  createProductsServices: true,
  createEditInvoicesProposals: true,
  sendInvoiceProposalLinks: true,
  applyInvoiceProposalPayments: true,

  // Purchase Orders
  createEditPurchaseOrders: true,

  // Credits and Retainers
  createApplyCreditsRetainers: true,

  // Reports (individual)
  reportPurchaseOrderStatus: true,
  reportItemTracker: true,
  reportRoomByRoom: true,
  reportSalesTax: true,
  reportTaskCompletion: true,
  reportVendorPayment: true,
  reportProfitDetail: true,
  reportTimeBilling: true,
  reportProfitByProject: true,
  reportClientPayment: true,
  reportARAging: true,
  reportAPAging: true,

  // Time Billing actions
  editTeamTimeEntries: true,
  createTimeBillingInvoices: true,
  editTimeBillingActivity: true,

  // Settings sub-pages
  settingsMyCompany: true,
  settingsMyTeam: true,
  settingsTaxLocations: true,
  settingsTimeBilling: true,
  settingsSalesTax: true,
  settingsQuickBooks: true,
  settingsTags: true,
};

// All granular (non-section) keys — used for "All Access" toggle
export const GRANULAR_PERMISSION_KEYS = [
  'createProductsServices',
  'createEditInvoicesProposals',
  'sendInvoiceProposalLinks',
  'applyInvoiceProposalPayments',
  'createEditPurchaseOrders',
  'createApplyCreditsRetainers',
  'reportPurchaseOrderStatus',
  'reportItemTracker',
  'reportRoomByRoom',
  'reportSalesTax',
  'reportTaskCompletion',
  'reportVendorPayment',
  'reportProfitDetail',
  'reportTimeBilling',
  'reportProfitByProject',
  'reportClientPayment',
  'reportARAging',
  'reportAPAging',
  'editTeamTimeEntries',
  'createTimeBillingInvoices',
  'editTimeBillingActivity',
  'settingsMyCompany',
  'settingsMyTeam',
  'settingsTaxLocations',
  'settingsTimeBilling',
  'settingsSalesTax',
  'settingsQuickBooks',
  'settingsTags',
];

// Grouped permission definitions for the Grant Access UI
export const PERMISSION_GROUPS = [
  {
    id: 'invoicesProposals',
    label: 'Invoices & Proposals',
    column: 'left',
    items: [
      { key: 'createProductsServices', label: 'Create & Edit Product and Services' },
      { key: 'createEditInvoicesProposals', label: 'Create & Edit Invoices and Proposals' },
      { key: 'sendInvoiceProposalLinks', label: 'Send Invoice & Proposal links' },
      { key: 'applyInvoiceProposalPayments', label: 'Apply Invoice & Proposal Payments' },
    ],
  },
  {
    id: 'purchaseOrders',
    label: 'Purchase Orders',
    column: 'right',
    items: [
      { key: 'createEditPurchaseOrders', label: 'Create & Edit Purchase Orders' },
    ],
  },
  {
    id: 'creditsRetainers',
    label: 'Credits and Retainers',
    column: 'right',
    items: [
      { key: 'createApplyCreditsRetainers', label: 'Create & Apply Credits and Retainers' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    column: 'left',
    items: [
      { key: 'reportPurchaseOrderStatus', label: 'Procurement Tracker' },
      { key: 'reportItemTracker', label: 'Item Tracker' },
      { key: 'reportRoomByRoom', label: 'Room by Room Item List' },
      { key: 'reportSalesTax', label: 'Sales Tax Report' },
      { key: 'reportTaskCompletion', label: 'Task Completion Report' },
      { key: 'reportVendorPayment', label: 'Vendor Payment Report' },
      { key: 'reportProfitDetail', label: 'Profit Detail by Project' },
      { key: 'reportTimeBilling', label: 'Time Billing Report' },
      { key: 'reportProfitByProject', label: 'Profit by Project' },
      { key: 'reportClientPayment', label: 'Client Payment Report' },
      { key: 'reportARAging', label: 'A/R Aging' },
      { key: 'reportAPAging', label: 'A/P Aging' },
    ],
  },
  {
    id: 'timeBillingGroup',
    label: 'Time Billing',
    column: 'right',
    items: [
      { key: 'editTeamTimeEntries', label: 'Edit Team Time Entries' },
      { key: 'createTimeBillingInvoices', label: 'Create Time Billing Invoices' },
      { key: 'editTimeBillingActivity', label: 'Edit Time Billing Activity' },
    ],
  },
  {
    id: 'settingsGroup',
    label: 'Settings',
    column: 'right',
    items: [
      { key: 'settingsMyCompany', label: 'My Company' },
      { key: 'settingsMyTeam', label: 'My Team' },
      { key: 'settingsTaxLocations', label: 'Tax Locations' },
      { key: 'settingsTimeBilling', label: 'Time Billing' },
      { key: 'settingsSalesTax', label: 'Sales Account' },
      { key: 'settingsQuickBooks', label: 'QuickBooks' },
      { key: 'settingsTags', label: 'Tags' },
    ],
  },
];

// Kept for backward compatibility
export const PERMISSION_LABELS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'projects', label: 'Projects' },
  { key: 'library', label: 'Products & Services' },
  { key: 'timeBilling', label: 'Time Billing' },
  { key: 'planner', label: 'Planner' },
  { key: 'reports', label: 'Reports' },
  { key: 'settings', label: 'Settings' },
];

export const ROUTE_PERMISSION_MAP = [
  { prefix: '/dashboard', key: 'dashboard' },
  { prefix: '/projects', key: 'projects' },
  { prefix: '/new-project', key: 'projects' },
  { prefix: '/products', key: 'library' },
  { prefix: '/timebilling', key: 'timeBilling' },
  { prefix: '/planner', key: 'planner' },
  // Vendors is a view of Planner contacts, so it shares Planner's access
  { prefix: '/vendors', key: 'planner' },
  // Report sub-routes (more specific before general)
  { prefix: '/reports/procurement-tracker', key: 'reportPurchaseOrderStatus' },
  { prefix: '/reports/item-tracker', key: 'reportItemTracker' },
  { prefix: '/reports/room-by-room', key: 'reportRoomByRoom' },
  { prefix: '/reports/sales-tax-report', key: 'reportSalesTax' },
  { prefix: '/reports/task-report', key: 'reportTaskCompletion' },
  { prefix: '/reports/vendor-payment', key: 'reportVendorPayment' },
  { prefix: '/reports/profit-detail', key: 'reportProfitDetail' },
  { prefix: '/reports/time-billing-report', key: 'reportTimeBilling' },
  { prefix: '/reports/tb-invoice-report', key: 'reportTimeBilling' },
  { prefix: '/reports/profit-by-project', key: 'reportProfitByProject' },
  { prefix: '/reports/client-payment', key: 'reportClientPayment' },
  { prefix: '/reports/ar-aging', key: 'reportARAging' },
  { prefix: '/reports/ap-aging', key: 'reportAPAging' },
  { prefix: '/reports', key: 'reports' },
  // Settings sub-routes (more specific before general)
  { prefix: '/settings/company', key: 'settingsMyCompany' },
  { prefix: '/settings/team', key: 'settingsMyTeam' },
  { prefix: '/settings/tax', key: 'settingsTaxLocations' },
  { prefix: '/settings/time-billing', key: 'settingsTimeBilling' },
  { prefix: '/settings/sales-account', key: 'settingsSalesTax' },
  { prefix: '/settings/quickbooks', key: 'settingsQuickBooks' },
  { prefix: '/settings/tags', key: 'settingsTags' },
  { prefix: '/settings', key: 'settings' },
];

export function getPermissionKeyForPath(pathname) {
  for (const { prefix, key } of ROUTE_PERMISSION_MAP) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      return key;
    }
  }
  return null;
}

function buildMergedPermissions(seatPermissions) {
  const merged = { ...DEFAULT_PERMISSIONS, ...seatPermissions };

  merged.dashboard = true;
  merged.projects = true;
  merged.library = true;
  merged.timeBilling = true;
  merged.planner = true;

  const settingsSubKeys = [
    'settingsMyCompany', 'settingsMyTeam', 'settingsTaxLocations',
    'settingsTimeBilling', 'settingsSalesTax', 'settingsQuickBooks', 'settingsTags',
  ];
  merged.settings = settingsSubKeys.some((k) => merged[k] !== false);

  const reportSubKeys = [
    'reportPurchaseOrderStatus', 'reportItemTracker', 'reportRoomByRoom',
    'reportSalesTax', 'reportTaskCompletion', 'reportVendorPayment',
    'reportProfitDetail', 'reportTimeBilling', 'reportProfitByProject', 'reportClientPayment',
  ];
  merged.reports = reportSubKeys.some((k) => merged[k] !== false);

  return merged;
}

function readPermissionsCache(userId) {
  try {
    const raw = localStorage.getItem(`permissions_cache_${userId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writePermissionsCache(userId, perms) {
  try {
    localStorage.setItem(`permissions_cache_${userId}`, JSON.stringify(perms));
  } catch {}
}

export function clearPermissionsCache(userId) {
  try {
    if (userId) {
      localStorage.removeItem(`permissions_cache_${userId}`);
    }
  } catch {}
}

export default function usePermissions() {
  const [permissions, setPermissions] = useState(() => {
    const userId = localStorage.getItem('ownUserId') || localStorage.getItem('userId');
    if (!userId) return DEFAULT_PERMISSIONS;
    return readPermissionsCache(userId) || DEFAULT_PERMISSIONS;
  });

  const [permissionsLoaded, setPermissionsLoaded] = useState(() => {
    const userId = localStorage.getItem('ownUserId') || localStorage.getItem('userId');
    if (!userId) return true;
    return readPermissionsCache(userId) !== null;
  });

  useEffect(() => {
    const userId = localStorage.getItem('ownUserId') || localStorage.getItem('userId');
    if (!userId) {
      setPermissionsLoaded(true);
      return;
    }

    fetch(`${API_BASE}/api/settings/${userId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.seatPermissions && typeof data.seatPermissions === 'object') {
          const merged = buildMergedPermissions(data.seatPermissions);
          writePermissionsCache(userId, merged);
          setPermissions(merged);
        }
        setPermissionsLoaded(true);
      })
      .catch(() => setPermissionsLoaded(true));
  }, []);

  return { permissions, permissionsLoaded };
}
